/**
 * Packaging Hierarchy Form Component
 * Simple form for setting case and pallet sizes during batch creation
 */

import { Input } from './ui';

export interface PackagingHierarchyData {
  unitsPerCase: number;
  casesPerPallet: number;
  caseDimensions?: string;
  palletDimensions?: string;
}

interface PackagingHierarchyFormProps {
  packagingData: Partial<PackagingHierarchyData>;
  onChange: (field: keyof PackagingHierarchyData, value: number) => void;
  disabled?: boolean;
  errors: { [key: string]: string };
}

export default function PackagingHierarchyForm({
  packagingData,
  onChange,
  disabled = false,
  errors
}: PackagingHierarchyFormProps) {

  const totalUnitsPerPallet = (packagingData.unitsPerCase || 0) * (packagingData.casesPerPallet || 0);

  return (
    <div>
      <h4 className="text-md font-medium text-gray-900 mb-4">Packaging Hierarchy</h4>
      <div className="space-y-4">
        {/* Units per Case */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Units per Case
            </label>
            <Input
              type="number"
              min="1"
              value={packagingData.unitsPerCase || ''}
              onChange={(e) => onChange('unitsPerCase', parseInt(e.target.value) || 0)}
              placeholder="e.g., 24"
              disabled={disabled}
              error={errors.unitsPerCase}
            />
            {errors.unitsPerCase && (
              <p className="mt-1 text-sm text-red-600">{errors.unitsPerCase}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              How many individual products fit in one case?
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cases per Pallet
            </label>
            <Input
              type="number"
              min="1"
              value={packagingData.casesPerPallet || ''}
              onChange={(e) => onChange('casesPerPallet', parseInt(e.target.value) || 0)}
              placeholder="e.g., 50"
              disabled={disabled}
              error={errors.casesPerPallet}
            />
            {errors.casesPerPallet && (
              <p className="mt-1 text-sm text-red-600">{errors.casesPerPallet}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              How many cases fit on one pallet?
            </p>
          </div>
        </div>

        {/* Preview Calculation */}
        {packagingData.unitsPerCase && packagingData.casesPerPallet && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h5 className="font-medium text-green-900 mb-3">Packaging Summary</h5>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="text-center">
                <p className="text-green-700 font-medium">Per Case</p>
                <p className="text-xl font-bold text-green-900">{packagingData.unitsPerCase}</p>
                <p className="text-green-600 text-xs">units</p>
              </div>
              <div className="text-center">
                <p className="text-green-700 font-medium">Per Pallet</p>
                <p className="text-xl font-bold text-green-900">{packagingData.casesPerPallet}</p>
                <p className="text-green-600 text-xs">cases</p>
              </div>
              <div className="text-center">
                <p className="text-green-700 font-medium">Total per Pallet</p>
                <p className="text-xl font-bold text-green-900">{totalUnitsPerPallet}</p>
                <p className="text-green-600 text-xs">units</p>
              </div>
            </div>
          </div>
        )}

        {/* Helper Text */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-xs text-blue-800">
            <strong>Tip:</strong> Setting packaging hierarchy enables efficient case and pallet-level 
            transfers in your supply chain. You can modify this later if needed.
          </p>
        </div>
      </div>
    </div>
  );
}